import React, { useState } from 'react';
import type { MouseEvent } from 'react';
import { 
    AppBar, 
    Toolbar, 
    Typography, 
    IconButton, 
    Box, 
    useTheme, 
    Menu, 
    MenuItem
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useDisplaySize } from '../../contexts/DisplaySizeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import privigoLogoDark from '../../assets/images/privigo-logo-dark.svg';
import privigoLogoWhite from '../../assets/images/privigo-logo-white.svg';
import SettingsModal from './SettingsModal';

const MANAGE_CLIENT = 'MANAGE_CLIENT';

const Navbar = () => {
  const theme = useTheme();
  const { size } = useDisplaySize();
  const { toggleColorMode, mode } = useColorMode();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null);
  const helpMenuOpen = Boolean(helpAnchor);

  const handleHelpOpen = (event: MouseEvent<HTMLElement>) => {
    setHelpAnchor(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchor(null);
  };

  const canManageClient = Boolean(user?.permissions?.includes(MANAGE_CLIENT));

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary
        }}
      >
        <Toolbar sx={{ 
          minHeight: size === 'extra small' ? '40px' : size === 'small' ? '48px' : size === 'large' ? '80px' : '64px',
          px: size === 'extra small' ? 0.5 : size === 'small' ? 1 : size === 'large' ? 3 : 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            padding: size === 'extra small' ? '2px 0' : size === 'small' ? '4px 0' : size === 'large' ? '12px 0' : '8px 0',
            '&:hover': {
              cursor: 'pointer',
              opacity: 0.9,
              transition: 'opacity 0.3s ease'
            }
          }}>
            <Box
              component="img"
              src={mode === 'dark' ? privigoLogoWhite : privigoLogoDark}
              alt="Privigo Logo"
              sx={{
                height: size === 'extra small' ? '28px' : size === 'small' ? '36px' : size === 'large' ? '60px' : '48px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Username Display */}
          {user && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mr: size === 'extra small' ? 1 : size === 'small' ? 1.5 : size === 'large' ? 3 : 2,
              px: size === 'extra small' ? 1 : size === 'small' ? 1.5 : size === 'large' ? 2 : 1.5,
              py: size === 'extra small' ? 0.25 : size === 'small' ? 0.5 : size === 'large' ? 1 : 0.75,
              backgroundColor: theme.palette.action.hover,
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <AccountCircleOutlinedIcon sx={{ 
                fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem',
                mr: size === 'extra small' ? 0.5 : size === 'small' ? 0.75 : size === 'large' ? 1 : 0.75,
                color: theme.palette.text.secondary
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: size === 'extra small' ? '0.625rem' : size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap'
                }}
              >
                {user.username}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: size === 'extra small' ? 0.25 : size === 'small' ? 0.5 : size === 'large' ? 1.5 : 1 }}>
            <IconButton 
              size="small" 
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { 
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              <NotificationsOutlinedIcon sx={{ fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }} />
            </IconButton>
            <IconButton 
              size="small" 
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { 
                  backgroundColor: theme.palette.action.hover
                }
              }}
              onClick={handleHelpOpen}
              aria-label="Help"
              aria-controls={helpMenuOpen ? 'help-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={helpMenuOpen ? 'true' : undefined}
            >
              <HelpOutlineIcon sx={{ fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }} />
            </IconButton>
            <Menu
              id="help-menu"
              anchorEl={helpAnchor}
              open={helpMenuOpen}
              onClose={handleHelpClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ dense: true }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/user-manual/overview');
                  handleHelpClose();
                }}
              >
                User manual
              </MenuItem>
              {canManageClient ? (
                <MenuItem
                  onClick={() => {
                    navigate('/api-docs');
                    handleHelpClose();
                  }}
                >
                  API docs
                </MenuItem>
              ) : null}
            </Menu>
            <IconButton 
              size="small" 
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { 
                  backgroundColor: theme.palette.action.hover
                }
              }}
              onClick={handleSettingsClick}
            >
              <SettingsIcon sx={{ fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }} />
            </IconButton>
            <IconButton 
              size="small" 
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { 
                  backgroundColor: theme.palette.action.hover
                }
              }}
              onClick={handleLogout}
            >
              <LogoutIcon sx={{ fontSize: size === 'extra small' ? '0.875rem' : size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem' }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <SettingsModal 
        open={settingsOpen} 
        onClose={handleSettingsClose} 
      />
    </>
  );
};

export default Navbar;
